import {
  BorderRadiusCorners,
  Box,
  Divider,
  Icon,
  Tag,
  Text,
  Touchable,
} from '@factorialco/deprecated-design-system'
import { Field } from 'factorial-form'

import { get12HFormat } from 'modules/core/lib/formatTime'
import i18n from 'modules/core/lib/i18n'

import PlannedBreaks from 'modules/shift_management/components/ShiftManagement/PlannedBreaks'
import ShiftAccessSelectors from 'modules/shift_management/components/ShiftManagement/ShiftAccessSelectors'
import ShiftLocationPicker from 'modules/shift_management/components/ShiftManagement/ShiftLocationPicker'
import ShiftNotesField from 'modules/shift_management/components/ShiftManagement/ShiftNotesField'
import ShiftOvertimeCheckbox from 'modules/shift_management/components/ShiftManagement/ShiftOvertimeCheckbox'
import UnsafeRadioInput from 'modules/shift_management/components/ShiftManagement/UnsafeRadioInput'
import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'
import type UnassignedShiftForm from 'modules/shift_management/lib/forms/UnassignedShiftForm'
import type PlannedBreak from 'modules/time_planning/store/internal/models/PlannedBreak'

interface Props {
  form: UnassignedShiftForm
  plannedBreaks: PlannedBreak[]
  onToggleShift: (id: string) => void
  readOnly?: boolean
  isAutomationEnabled?: boolean
  locationFilterValue: Field
  workAreaFilterValue: Field
}

const DefaultShiftItem = ({
  form,
  plannedBreaks,
  onToggleShift,
  isAutomationEnabled = false,
  locationFilterValue,
  workAreaFilterValue,
}: Props) => {
  const { isTimeFormat24H } = useIsTimeFormat24H()

  const startAtValue = form.get('start_at').value
  const startAt = isTimeFormat24H ? startAtValue : get12HFormat(startAtValue)
  const endAtValue = form.get('end_at').value
  const endAt = isTimeFormat24H ? endAtValue : get12HFormat(endAtValue)

  const isSelected = form.get('is_selected').value

  const onSelect = () => {
    onToggleShift(form.get('id').value)
  }

  const borderColor = isSelected ? 'viridian1000' : 'grey400'
  const borderRadius: BorderRadiusCorners = { all: 'abs008' }

  if (isAutomationEnabled) {
    const locationFormField = form.get('location_id')
    const workAreaFormField = form.get('work_area_id')
    locationFormField.set(locationFilterValue)
    workAreaFormField.set(workAreaFilterValue)
  }

  return (
    <Box
      border={{
        all: {
          width: 's2',
          style: 'solid',
          color: borderColor,
        },
      }}
      borderRadius={borderRadius}
      background="grey000White"
    >
      <Box
        alignItems="center"
        borderRadius={borderRadius}
        padding="s16"
        flexDirection="row"
        justifyContent="spaceBetween"
        background="grey000White"
      >
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" gap="s4">
          <Icon.Small icon="circle" color={form.get('color').value || 'grey400'} />
          <Box maxWidth="s140">
            <Text size="300" weight="semibold" color="grey800" ellipsis>
              {form.get('title').value}
            </Text>
          </Box>
          <Box alignItems="center" flexDirection="row" gap="s4">
            {startAt} {i18n.t('common.to').toLowerCase()} {endAt}
            {form.get('overnight_shift').value && (
              <Tag type={Tag.Types.grey}>{i18n.t('shift_management.plus_1_day_tag')}</Tag>
            )}
          </Box>
        </Box>

        <Touchable onClick={onSelect}>
          <UnsafeRadioInput selected={isSelected} />
        </Touchable>
      </Box>

      {isSelected && (
        <Box gap="s16">
          <Box paddingX="s16">
            <Divider />
          </Box>
          <Box background="grey000White" borderRadius={borderRadius}>
            {!!plannedBreaks.length && (
              <Box padding="s16">
                <PlannedBreaks plannedBreaks={plannedBreaks} showDeleted canManage />
              </Box>
            )}

            <Box paddingX="s16">
              <ShiftOvertimeCheckbox form={form} />
            </Box>

            {!isAutomationEnabled && (
              <Box padding="s16" gap="s16">
                <ShiftLocationPicker
                  locationField={form.get('location_id')}
                  workAreaField={form.get('work_area_id')}
                  hideNoLocationOption
                />
                <ShiftNotesField notesField={form.get('notes')} />
              </Box>
            )}

            <Box paddingX="s16" paddingY="s12">
              <ShiftAccessSelectors form={form} />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default DefaultShiftItem
